import React from 'react';
import styled from '@benzinga/themetron';
import { StoryObject } from '@benzinga/advanced-news-manager';
import { BzImage } from '@benzinga/image';
import { ArticleData } from '@benzinga/article-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { formatImageUrl } from '@benzinga/utils';

export interface RelatedArticlesProps {
  className?: string;
  excludedArticles?: number[];
  nodes: ArticleData[];
  // queryBy?: 'Channels' | 'Tickers' | 'Tags';
  includeImages?: boolean;
  pageCount?: number;
  viewMoreLink?: string;
}

export const RelatedArticles: React.FC<RelatedArticlesProps> = ({
  className = '',
  excludedArticles,
  includeImages = true,
  nodes,
  pageCount = 5,
  viewMoreLink,
}) => {
  let filteredNodes = excludedArticles
    ? nodes.filter(node => node.nodeId && !excludedArticles.includes(node.nodeId))
    : nodes;
  if (!filteredNodes.length) return <></>;
  if (filteredNodes.length > pageCount) {
    filteredNodes = filteredNodes.slice(0, pageCount);
  }

  return (
    <Container className={`top-stories ${className}`}>
      <div className="header-container">
        <h2>Trending Articles</h2>
      </div>
      <div className="stories-list">
        {filteredNodes.map((node: ArticleData, index: number) => {
          const image = formatImageUrl(node as unknown as StoryObject);
          return (
            <div className="stories-list-item" key={index}>
              <div>
                <a
                  className="story-link"
                  data-action="Clicked Related Article"
                  href={`/${node.canonicalPath}`}
                  rel="noreferrer"
                >
                  {image && includeImages && (
                    <ImageWrapper className="image-wrapper">
                      <BzImage alt="" height={70} layout="fixed" objectFit="cover" src={image} width={70} />
                    </ImageWrapper>
                  )}
                  <div>
                    <h3 className="story-title">{node.title}</h3>
                    <DoubleLine className="story-subtitle">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(node.metaProps?.description ?? node.teaserText),
                        }}
                      />
                    </DoubleLine>
                  </div>
                </a>
              </div>
            </div>
          );
        })}
      </div>
      {viewMoreLink && (
        <a href={viewMoreLink} style={{ marginTop: '5px' }}>
          View more
        </a>
      )}
    </Container>
  );
};

const ImageWrapper = styled.div`
  &.image-wrapper {
    margin-right: 10px;
  }
`;

// TODO: Replace w/ shared styled component
const DoubleLine = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Container = styled.div`
  &.top-stories {
    .header-container {
      display: flex;
      align-items: center;
      margin-bottom: 0.75rem;

      > h2 {
        z-index: 2;
        font-weight: 600;
        font-size: 1.25rem;
        line-height: 1;
        padding-right: 0.5rem;
        white-space: nowrap;
        margin-bottom: 4px;
      }
    }

    .stories-list {
      > .stories-list-item {
        display: block;

        > div {
          display: flex;
          align-items: center;
          margin-bottom: 0.7rem;

          &:hover {
            opacity: 0.9;
            a {
              color: ${({ theme }) => theme.colors.brandLight};
            }
          }

          a {
            display: flex;
            color: ${({ theme }) => theme.colors.brand};
            text-decoration: none;
          }

          .story-image {
            display: block;
            position: relative;
            width: 70px;
            height: 70px;
            min-width: 70px;
            margin-right: 0.8125rem;
          }

          .story-title {
            font-size: ${({ theme }) => theme.fontSize.lg};
            font-weight: ${({ theme }) => theme.fontWeight.bold};
            line-height: 1.4rem;
            color: #2ca2d1;
            margin: 0;
            margin-bottom: 4px;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
          }

          .story-subtitle {
            color: ${({ theme }) => theme.colors.foreground};
            font-size: ${({ theme }) => theme.fontSize.sm};
            line-height: ${({ theme }) => theme.lineHeight.normal};
            font-weight: ${({ theme }) => theme.fontWeight.normal};
          }
        }
      }
    }
  }
`;

export default RelatedArticles;
